import { attributeStatesFromStore, initialAnswersForStore } from './answers';
import type { Role, Store } from '../data/types';
import type { PublishedTemplate } from '../data/templates';
import { getTemplateById } from '../data/templates';
import {
  buildAnswersFromRows,
  claimServerAudit,
  getAttachmentsForAudit,
  getAuditState,
  getNotesForAudit,
  getResponsesForAudit,
  getSubmittedAuditsForStore,
  setServerAuditStatus,
  type AuditAttachmentRow,
  type ServerAudit,
} from '../data/audits';
import { getTasksForAudit } from '../data/tasks';
import { sessionStore, getSyncEngine } from '../local';
import { closedElsewhereReason, unsentItems } from '../local/syncEngine';
import { ownsSession } from '../local/ownership';
import type {
  LocalAttachment,
  LocalAuditSession,
  LocalNote,
  LocalTaskDraft,
} from '../local/types';
import { AUDIT_EXPIRY_HOURS, isAuditExpired } from './expiry';
import { hasKpis } from './kpis';
import { buildKpiContext, KPI_CONTEXT_MAX_AGE_MS } from './kpiContext';
import type { KpiSettingsMap } from './budgets';
import { getStoreBudgets } from '../data/storeBudgets';
import { familyKey } from '../data/templateAdmin';
import { easternIsoDay } from '../data/taskRules';

/**
 * Audit lifecycle (spec §9): start → answer continuously (local-first) →
 * leave/resume anytime → submit. This module orchestrates session creation;
 * conducting and submitting live in the Conduct page + session store.
 */

/**
 * Start a fresh audit for a store: template snapshot + tri-state gate
 * pre-fill (known attributes answer their gates; unknown ones surface a
 * data-incomplete warning on the conduct page).
 */
export async function startAudit(
  store: Store,
  template: PublishedTemplate,
  auditorUpn: string,
  conductorRole: Role,
  /** The signed-in account (not "View as") — owns the copy on this device. */
  ownerUpn: string,
): Promise<LocalAuditSession> {
  const { answers, missingAttributes } = initialAnswersForStore(
    template.definition,
    attributeStatesFromStore(store),
  );
  const session = await sessionStore.startSession({
    storeId: Number(store.id),
    storeNumber: store.storeNumber,
    storeName: store.name,
    templateId: template.id,
    templateVersion: template.version,
    templateName: template.name,
    definition: template.definition,
    settings: template.settings,
    frequencyDays: template.frequencyDays,
    auditorUpn,
    ownerUpn,
    conductorRole,
    initialAnswers: answers,
    missingAttributes,
    storeAccountUpn: store.managerEmail,
  });
  getSyncEngine().kick();
  // KPI hints (B8): read now, while there's signal — never holds up the start.
  void loadKpiContext(session, template.kpiSettings ?? null).catch(() => undefined);
  return session;
}

/**
 * B8 (owner rulings 2026-10-25): snapshot the store's last KPI values, its
 * budgets for this year and the template's KPI settings onto the session,
 * so the form can show "Last visit / Budget / Big change" with no signal.
 * Best effort: a failed read leaves the hints off. `settings` undefined =
 * read the template row for them. Returns the updated session (or
 * undefined when nothing was stored).
 */
export async function loadKpiContext(
  session: LocalAuditSession,
  settings?: KpiSettingsMap | null,
  now: Date = new Date(),
): Promise<LocalAuditSession | undefined> {
  if (!hasKpis(session.definition)) return undefined;
  const today = easternIsoDay(now);
  const year = Number(today.slice(0, 4));
  const [audits, budgetRows, kpiSettings] = await Promise.all([
    getSubmittedAuditsForStore(session.storeId, { kpisOnly: true }),
    getStoreBudgets(session.storeId).catch(() => []),
    settings !== undefined
      ? Promise.resolve(settings)
      : getTemplateById(session.templateId).then((t) => t?.kpiSettings ?? null).catch(() => null),
  ]);
  const context = buildKpiContext({
    definition: session.definition,
    // Budgets are stored under the template FAMILY key (Budgets & goals writes familyKey(auditType)).
    templateKey: familyKey(session.definition.auditType ?? ''),
    settings: kpiSettings,
    audits: audits
      .filter((a) => a.id !== session.serverId && a.submittedAt)
      // The day the values were typed (start), like the form and the tables.
      .map((a) => ({ day: easternIsoDay(new Date(a.startedAt || a.submittedAt)), kpis: a.kpis })),
    budgets: budgetRows.find((r) => r.year === year)?.budgets ?? null,
    year,
    now,
  });
  return sessionStore.setKpiContext(session.localId, context);
}

/** The snapshot is missing or older than KPI_CONTEXT_MAX_AGE_MS (the conduct page re-reads it when online). */
export function kpiContextStale(session: LocalAuditSession, now: Date = new Date()): boolean {
  if (!hasKpis(session.definition)) return false;
  const at = session.kpiContext ? Date.parse(session.kpiContext.fetchedAt) : NaN;
  return Number.isNaN(at) || now.getTime() - at > KPI_CONTEXT_MAX_AGE_MS;
}

/**
 * Adopt an in-progress audit that lives on the server but not on this
 * device (cross-device resume). This device CLAIMS the audit first, so the
 * previous device stops pushing answers over the newer ones on its next
 * sync. Only SYNCED work comes across — the caller must surface that
 * plainly to the auditor (spec §8). Notes, tasks and files the other
 * device uploaded come along for display (already synced, never re-sent).
 */
export async function resumeFromServer(
  serverAudit: ServerAudit,
  store: Store,
  auditorUpn: string,
  conductorRole: Role,
  ownerUpn: string,
): Promise<LocalAuditSession> {
  if (isAuditExpired(serverAudit.startedAt)) {
    await setServerAuditStatus(serverAudit.id, 'expired').catch(() => undefined);
    throw new Error(
      `This audit expired (${AUDIT_EXPIRY_HOURS} hours after it was started) and can no longer be continued.`,
    );
  }
  const template = await getTemplateById(serverAudit.templateId);
  if (!template) {
    throw new Error(
      'The form this audit was started from no longer exists.',
    );
  }
  if (template.version !== serverAudit.templateVersion) {
    // Published versions are immutable, so a mismatch means the row was
    // replaced (dev-only -Force). The definition may differ from what the
    // original device saw; proceed with what the server has.
    console.warn(
      `Audit ${serverAudit.id}: template version mismatch (audit v${serverAudit.templateVersion}, row v${template.version})`,
    );
  }

  const localId = crypto.randomUUID();
  await claimServerAudit(serverAudit.id, localId);

  const [rows, notes, tasks, files] = await Promise.all([
    getResponsesForAudit(serverAudit.id),
    getNotesForAudit(serverAudit.id),
    getTasksForAudit(serverAudit.id),
    getAttachmentsForAudit(serverAudit.id).catch(() => [] as AuditAttachmentRow[]),
  ]);
  const { answers, responseIds } = buildAnswersFromRows(rows);

  const session = await sessionStore.startSession({
    localId,
    storeId: serverAudit.storeId,
    storeNumber: store.storeNumber,
    storeName: store.name,
    templateId: serverAudit.templateId,
    templateVersion: serverAudit.templateVersion,
    templateName: template.name,
    definition: template.definition,
    settings: template.settings,
    frequencyDays: template.frequencyDays,
    auditorUpn,
    ownerUpn,
    conductorRole,
    initialAnswers: answers,
    serverId: serverAudit.id,
    responseIds,
    storeAccountUpn: store.managerEmail,
    ...(serverAudit.startedAt ? { startedAt: serverAudit.startedAt } : {}),
    notes: notes.map(
      (n): LocalNote => ({
        id: `server-note-${n.id}`,
        questionId: n.questionId,
        ...(n.sampleIndex !== null ? { sampleIndex: n.sampleIndex } : {}),
        text: n.text,
        author: n.author,
        createdAt: n.created,
        serverId: n.id,
      }),
    ),
    taskDrafts: tasks.map(
      (t): LocalTaskDraft => ({
        id: `server-task-${t.id}`,
        questionId: t.questionId ? t.questionId : null,
        ...(t.sampleIndex !== null ? { sampleIndex: t.sampleIndex } : {}),
        title: t.title,
        description: t.description,
        assignedTo: t.assignedTo,
        dueDate: t.dueDate,
        createdBy: t.createdBy,
        createdAt: '',
        serverId: t.id,
      }),
    ),
    attachments: files.map((f): LocalAttachment => {
      const kind = f.kind === 'pdf' ? 'pdf' : 'photo';
      return {
        id: `server-file-${f.id}`,
        questionId: f.questionId,
        ...(f.sampleIndex !== null ? { sampleIndex: f.sampleIndex } : {}),
        kind,
        filename: f.filename,
        mimeType: kind === 'pdf' ? 'application/pdf' : 'image/jpeg',
        size: 0,
        uploadedBy: f.uploadedBy,
        createdAt: '',
        serverItemId: f.id,
        url: f.url,
      };
    }),
  });
  void loadKpiContext(session, template.kpiSettings ?? null).catch(() => undefined);
  return session;
}

/**
 * Bring this device's in-progress copies in line with the server (run
 * when the Audits hub has loaded my open server rows):
 * - repair start dates drifted by pre-fix cross-device resumes
 * - expire copies past their 72 hours
 * - close copies another device has continued, submitted, cancelled or
 *   expired, so they can't be edited here by mistake
 * Returns the (possibly updated) sessions in the same order.
 */
export async function reconcileWithServer(
  local: LocalAuditSession[],
  server: ServerAudit[],
): Promise<LocalAuditSession[]> {
  const byId = new Map(server.map((a) => [a.id, a]));
  let changed = false;
  const result = await Promise.all(
    local.map(async (original) => {
      let s = original;
      if (s.status !== 'in_progress') return s;
      const row = s.serverId !== null ? byId.get(s.serverId) : undefined;

      const truth = row?.startedAt;
      if (truth && !Number.isNaN(Date.parse(truth)) &&
          Date.parse(truth) !== Date.parse(s.startedAt)) {
        s = (await sessionStore.correctStartedAt(s.localId, truth)) ?? s;
      }
      if (isAuditExpired(s.startedAt)) {
        changed = true;
        return sessionStore.closeSession(s.localId, 'expired');
      }
      if (s.serverId === null) return s;

      if (row) {
        if (row.originLocalId && row.originLocalId !== s.localId) {
          changed = true;
          return sessionStore.markClosedElsewhere(s.localId, 'continued');
        }
        return s;
      }
      // Not among my open rows: closed (or gone) on the server.
      const state = await getAuditState(s.serverId).catch(() => undefined);
      if (state === undefined) return s; // couldn't tell — leave it alone
      const reason = closedElsewhereReason(s, state);
      if (!reason) return s;
      changed = true;
      return sessionStore.markClosedElsewhere(s.localId, reason);
    }),
  );
  if (changed) getSyncEngine().kick();
  return result;
}

/**
 * Conduct-page guard (online, best-effort): has another device taken this
 * audit over or closed it since this copy was last synced?
 */
export async function checkStillOwned(
  session: LocalAuditSession,
): Promise<LocalAuditSession> {
  if (session.status !== 'in_progress' || session.serverId === null) return session;
  const state = await getAuditState(session.serverId).catch(() => undefined);
  if (state === undefined) return session;
  const reason = closedElsewhereReason(session, state);
  return reason ? sessionStore.markClosedElsewhere(session.localId, reason) : session;
}

/** Cancel an audit on this device (works offline; syncs as 'cancelled'). */
export async function cancelLocalAudit(localId: string): Promise<LocalAuditSession> {
  const closed = await sessionStore.closeSession(localId, 'cancelled');
  getSyncEngine().kick(0);
  return closed;
}

/** Cancel an audit started on another device (online only). */
export async function cancelServerAudit(auditId: number): Promise<void> {
  await setServerAuditStatus(auditId, 'cancelled');
}

/**
 * How long past its 72 hours a server row nobody here tracks is left alone
 * before it is marked 'expired'. Another device may hold it submitted
 * offline before the deadline and still be syncing (a submit before the
 * deadline counts, docs/offline-sync.md) — marking it expired first would
 * freeze the row and lose that audit. A week covers any realistic offline
 * stretch; after that it's an abandoned start (e.g. a lost phone).
 */
export const SERVER_EXPIRY_GRACE_DAYS = 7;

/**
 * My open server rows, past their 72 hours, are dropped from the list.
 * - Rows a local session here tracks are left to this device's own sync
 *   engine, which knows whether they were submitted in time — never
 *   written from here.
 * - Rows no device here tracks are marked 'expired' (best effort) only
 *   once SERVER_EXPIRY_GRACE_DAYS have also passed.
 * Returns the still-live rows.
 */
export async function dropExpiredServerAudits(
  rows: ServerAudit[],
  local: readonly LocalAuditSession[],
  now: number = Date.now(),
): Promise<ServerAudit[]> {
  const trackedIds = new Set(local.map((s) => s.serverId).filter((id): id is number => id !== null));
  const localIds = new Set(local.map((s) => s.localId));
  const live: ServerAudit[] = [];
  for (const row of rows) {
    const tracked =
      trackedIds.has(row.id) || (row.originLocalId !== null && localIds.has(row.originLocalId));
    if (tracked || !isAuditExpired(row.startedAt, now)) {
      live.push(row);
    } else if (isAuditExpired(row.startedAt, now - SERVER_EXPIRY_GRACE_DAYS * 86_400_000)) {
      await setServerAuditStatus(row.id, 'expired').catch(() => undefined);
    }
  }
  return live;
}

/**
 * Local sessions that represent real audits (not previews) and belong to
 * the signed-in account on this device, newest first (shared devices —
 * src/local/ownership.ts).
 */
export async function listMyLocalAudits(signedInUpn: string): Promise<LocalAuditSession[]> {
  const all = await sessionStore.listSessions();
  return all.filter((s) => !s.previewOnly && ownsSession(s, signedInUpn));
}

/**
 * Sign-out on a shared device (owner ruling 2026-10-18): what this account
 * still has on the device. `unsent` = changes not yet sent (kept — they sync
 * at this account's next sign-in here); `clearable` = saved audits with
 * nothing left to send, which can be removed (an in-progress one can be
 * resumed from the server on any device).
 */
export async function myDeviceAudits(
  signedInUpn: string,
): Promise<{ unsent: number; clearable: LocalAuditSession[] }> {
  const mine = await listMyLocalAudits(signedInUpn);
  return {
    unsent: mine.reduce((n, s) => n + unsentItems(s), 0),
    clearable: mine.filter((s) => unsentItems(s) === 0),
  };
}

/** Remove saved audits (and their photo bytes) from this device. */
export async function removeFromDevice(sessions: readonly LocalAuditSession[]): Promise<void> {
  for (const s of sessions) await sessionStore.deleteSession(s.localId);
}

